import { useEffect, useState } from 'react';
import { LoadMore } from '@/components/lists';
import type { ArClaim } from '../types';
import { lateReasonOf, money } from '../utils';
import { WithinUsual } from './WithinUsual';

interface Props {
  /** The "past the payer's usual time" queue, oldest first. */
  lateRows: ArClaim[];
  /** The "within the payer's usual time" queue — grouped by payer in the UI. */
  ontimeRows: ArClaim[];
  selectedId?: string;
  search: string;
  /** '' = all payers, a payer name, or MULTI_PAYER when a link filtered to several. */
  payer: string;
  /** Every payer with its REAL claim count under the other active filters. */
  payerOptions: { name: string; count: number }[];
  /** Payers a link asked for (`?payer=aetna,delta`); their groups open by default. */
  focusPayers: string[];
  multiPayerCount: number;
  /** "ELVA isn't following up" (Oct 22): paused, stopped, or billed elsewhere. Still owed. */
  notFollowedRows: ArClaim[];
  /** "The payer pays the patient" (D197): not counted in what insurers owe. */
  paysRows: ArClaim[];
  /** `?group=pays-patient` opens the group expanded. */
  openPays: boolean;
  onSearch: (v: string) => void;
  onPayerSelect: (v: string) => void;
  onSelect: (id: string) => void;
}

function Row({ claim, selected, onSelect }: { claim: ArClaim; selected: boolean; onSelect: (id: string) => void }) {
  // Why the claim is late (D311): the payer's usual time, the practice's threshold, or a state deadline.
  const why = claim.group === 'late' ? lateReasonOf(claim) : null;
  return (
    <button type="button" className={`iar-row${selected ? ' on' : ''}`} aria-current={selected || undefined} onClick={() => { onSelect(claim.id); }}>
      <span className="iar-rtop">
        <strong className="iar-rname">{claim.patient}</strong>
        <span className="iar-ramt num">{money(claim.billed)}</span>
      </span>
      <span className="iar-rpayer">{claim.payer.name} · sent {claim.sentDisplay}</span>
      <span className="iar-rline">
        {/* "Needs a person" is read from the shared hitlItemId flag (the claim
            is in Human in the loop), never hardcoded independently of it. */}
        {claim.hitlItemId ? (
          <span className="iar-badge b-bad">Needs a person</span>
        ) : claim.rowBadge === 'late' ? (
          <span className="iar-badge b-warn">Late</span>
        ) : claim.rowBadge === 'paused' ? (
          <span className="iar-badge b-mute">Paused</span>
        ) : claim.rowBadge === 'not-following' ? (
          <span className="iar-badge b-mute">Not following up</span>
        ) : null}
        <span>{claim.rowLine}</span>
      </span>
      {why ? <span className="iar-rwhy">{why.line}</span> : null}
      {claim.linkedLine ? <span className="iar-rlink">{claim.linkedLine}</span> : null}
      <span className="iar-rmeta">{claim.rowMeta}</span>
    </button>
  );
}

/** A row in "The payer pays the patient": billed (never owed), the Pays the patient badge, no age bucket. */
function PaysRow({ claim, selected, onSelect }: { claim: ArClaim; selected: boolean; onSelect: (id: string) => void }) {
  return (
    <button type="button" className={`iar-row${selected ? ' on' : ''}`} aria-current={selected || undefined} onClick={() => { onSelect(claim.id); }}>
      <span className="iar-rtop">
        <strong className="iar-rname">{claim.patient}</strong>
        <span className="iar-ramt num">{money(claim.billed)} <span className="iar-billed">billed</span></span>
      </span>
      <span className="iar-rpayer">{claim.payer.name} · sent {claim.sentDisplay}</span>
      <span className="iar-rline">
        <span className="iar-badge b-mute">Pays the patient</span>
        <span>{claim.rowMeta}</span>
      </span>
    </button>
  );
}

const PAGE = 50;
export const MULTI_PAYER = '__multi';

export function List({
  lateRows, ontimeRows, selectedId,
  search, payer, payerOptions, focusPayers, multiPayerCount, notFollowedRows, paysRows, openPays, onSearch, onPayerSelect, onSelect,
}: Props) {
  // Late is a queue: show all, oldest first, load more by 50 (00-ds-09 §9).
  const [lateLoaded, setLateLoaded] = useState(PAGE);
  const lateShown = lateRows.slice(0, lateLoaded);
  const [nfLoaded, setNfLoaded] = useState(PAGE);
  const nfShown = notFollowedRows.slice(0, nfLoaded);
  const nfCount = notFollowedRows.length;
  const nfTotal = notFollowedRows.reduce((s, c) => s + c.balance, 0);

  // Group counts come from the same (filtered) records the rows come from.
  const lateCount = lateRows.length;
  const lateTotal = lateRows.reduce((s, c) => s + c.balance, 0);
  const ontimeCount = ontimeRows.length;
  const ontimeTotal = ontimeRows.reduce((s, c) => s + c.balance, 0);
  const paysCount = paysRows.length;
  const paysBilled = paysRows.reduce((s, c) => s + c.billed, 0);

  // Collapsed by default; a link (?group=pays-patient) or a selected claim in it opens it.
  const [paysOpen, setPaysOpen] = useState(openPays);
  const selectedInPays = selectedId !== undefined && paysRows.some((c) => c.id === selectedId);
  useEffect(() => {
    if (openPays || selectedInPays) setPaysOpen(true);
  }, [openPays, selectedInPays]);

  return (
    <aside className="iar-list" aria-label="Claims waiting for payment">
      <div className="iar-filters">
        <label style={{ flex: '1 1 auto' }}>
          <span className="iar-vh">Search claims</span>
          <input className="iar-field" type="search" placeholder="Patient, claim no., member ID or amount" value={search} onChange={(e) => { onSearch(e.target.value); }} />
        </label>
        <label>
          <span className="iar-vh">Payer</span>
          <select className="iar-sel" value={payer} onChange={(e) => { onPayerSelect(e.target.value); }}>
            <option value="">All payers</option>
            {payer === MULTI_PAYER ? <option value={MULTI_PAYER}>{multiPayerCount} payers</option> : null}
            {payerOptions.map((p) => <option key={p.name} value={p.name}>{p.name} ({p.count})</option>)}
          </select>
        </label>
      </div>

      {lateRows.length > 0 ? (
        <>
          <h2 className="iar-grp" id="iar-late-grp">Past the payer’s usual time · {lateCount} · {money(lateTotal)}</h2>
          {lateShown.map((c) => <Row key={c.id} claim={c} selected={selectedId === c.id} onSelect={onSelect} />)}
          <LoadMore loaded={lateShown.length} total={lateRows.length} onMore={() => { setLateLoaded((n) => n + PAGE); }} />
        </>
      ) : null}

      {ontimeRows.length > 0 ? (
        <>
          <h2 className="iar-grp">Within the payer’s usual time · {ontimeCount} · {money(ontimeTotal)}</h2>
          <WithinUsual
            rows={ontimeRows}
            query={search}
            focusPayers={focusPayers}
            {...(selectedId ? { selectedId } : {})}
            onSelect={onSelect}
          />
        </>
      ) : null}

      {nfCount > 0 ? (
        <>
          <h2 className="iar-grp" id="iar-nf-grp">ELVA isn’t following up · {nfCount} · {money(nfTotal)}</h2>
          {nfShown.map((c) => <Row key={c.id} claim={c} selected={selectedId === c.id} onSelect={onSelect} />)}
          <LoadMore loaded={nfShown.length} total={nfCount} onMore={() => { setNfLoaded((n) => n + PAGE); }} />
        </>
      ) : null}

      {paysCount > 0 ? (
        <>
          <h2 className="iar-grp-h" id="iar-pays-grp" style={{ margin: 0 }}>
            <button type="button" className="iar-grp-btn" aria-expanded={paysOpen} aria-controls="iar-pays-body" onClick={() => { setPaysOpen((o) => !o); }}>
              <span className={`iar-grp-caret${paysOpen ? ' open' : ''}`} aria-hidden>▸</span>
              <span>The payer pays the patient · {paysCount} · {money(paysBilled)} billed</span>
            </button>
          </h2>
          {paysOpen ? (
            <div id="iar-pays-body">
              <p className="iar-notice iar-pp-note">
                Dr. Lee is out of network with these payers and there’s no assignment of benefits on file, so they pay the patient. ELVA still follows each claim, so you know when the patient has been paid. Not counted in what insurers owe you.
              </p>
              {paysRows.map((c) => <PaysRow key={c.id} claim={c} selected={selectedId === c.id} onSelect={onSelect} />)}
            </div>
          ) : null}
        </>
      ) : null}

      {lateRows.length === 0 && ontimeRows.length === 0 && nfCount === 0 && paysCount === 0 ? (
        <p className="iar-meta" style={{ padding: '18px 16px' }}>No claims match.</p>
      ) : null}
    </aside>
  );
}
